import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/auth.service';

interface DashboardStats {
  activeProjects: number;
  totalClients: number;
  monthlyRevenue: number;
  averageProgress: number;
  recentProjects: {
    id: string;
    name: string;
    status: string;
    progress: number;
    budget: number;
    clientName: string;
    updatedAt: number;
  }[];
  recentActivity: {
    id: string;
    action: string;
    userName: string;
    timestamp: number;
    details: any;
  }[];
  recentDocuments: {
    id: string;
    name: string;
    type: string;
    projectName: string;
    uploadedBy: string;
    signed: boolean;
    createdAt: number;
  }[];
  alerts: {
    overdueInvoices: number;
    pendingChangeOrders: number;
    staleProjects: number;
  };
}

export function SysAdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await authService.fetchWithAuth('/api/admin/dashboard');
      setStats(response.data);
    } catch (err: any) {
      console.error('Error loading dashboard:', err);
      setError(err.message || 'Error al cargar el dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { class: string; label: string }> = {
      planning: { class: 'status-pending', label: 'Planificación' },
      in_progress: { class: 'status-progress', label: 'En progreso' },
      on_hold: { class: 'status-pending', label: 'En espera' },
      completed: { class: 'status-completed', label: 'Completado' },
      cancelled: { class: 'status-inactive', label: 'Cancelado' },
      signed: { class: 'status-signed', label: 'Firmado' },
      pending: { class: 'status-pending', label: 'Pendiente' },
    };
    return badges[status] || { class: 'status-pending', label: status };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatRelativeTime = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `Hace ${minutes} min`;
    if (hours < 24) return `Hace ${hours}h`;
    if (days < 7) return `Hace ${days}d`;
    return new Date(timestamp).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatActivityAction = (action: string) => {
    const actions: Record<string, string> = {
      change_approved: 'Orden de cambio aprobada',
      change_rejected: 'Orden de cambio rechazada',
      change_executed: 'Orden de cambio ejecutada',
      login: 'Inicio de sesión',
      logout: 'Cierre de sesión',
    };
    return actions[action] || action.replace(/_/g, ' ');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <span className="error-icon">❌</span>
        <span>{error}</span>
        <button onClick={loadDashboardData} className="btn-action ml-4">
          🔄 Reintentar
        </button>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12 text-gray-500">
        No hay datos disponibles
      </div>
    );
  }

  return (
    <div>
      {/* Welcome Card */}
      <div className="welcome-card">
        <h2>Bienvenido, {user?.name || 'Administrador'} 🏗️</h2>
        <p>Panel de control del sistema - Administrador del Sistema</p>
      </div>

      {/* Alertas del Sistema */}
      {(stats.alerts.overdueInvoices > 0 || 
        stats.alerts.pendingChangeOrders > 0 || 
        stats.alerts.staleProjects > 0) && (
        <div className="mb-5 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h3 className="text-sm font-bold text-yellow-800 mb-2">
            ⚠️ Alertas del Sistema
          </h3>
          <div className="flex flex-wrap gap-4 text-sm">
            {stats.alerts.overdueInvoices > 0 && (
              <span className="text-red-700">
                🧾 {stats.alerts.overdueInvoices} facturas vencidas
              </span>
            )}
            {stats.alerts.pendingChangeOrders > 0 && (
              <span className="text-yellow-700">
                🔄 {stats.alerts.pendingChangeOrders} órdenes de cambio pendientes
              </span>
            )}
            {stats.alerts.staleProjects > 0 && (
              <span className="text-orange-700">
                ⏸️ {stats.alerts.staleProjects} proyectos sin avances recientes
              </span>
            )}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-icon">📋</span>
          <span className="stat-value">{stats.activeProjects}</span>
          <span className="stat-label">Proyectos Activos</span>
        </div>
        <div className="stat-card">
          <span className="stat-icon">👥</span>
          <span className="stat-value">{stats.totalClients}</span>
          <span className="stat-label">Clientes Activos</span>
        </div>
        <div className="stat-card">
          <span className="stat-icon">💰</span>
          <span className="stat-value">{formatCurrency(stats.monthlyRevenue)}</span>
          <span className="stat-label">Ingresos del Mes</span>
        </div>
        <div className="stat-card">
          <span className="stat-icon">📊</span>
          <span className="stat-value">{stats.averageProgress}%</span>
          <span className="stat-label">Avance Promedio</span>
        </div>
      </div>

      {/* Grid de 2 columnas */}
      <div className="dashboard-row">
        <div className="card">
          <h3>📋 Proyectos Recientes</h3>
          {stats.recentProjects.length === 0 ? (
            <p className="text-gray-500 text-sm py-4">No hay proyectos registrados</p>
          ) : (
            <div className="project-list">
              {stats.recentProjects.map((project) => {
                const badge = getStatusBadge(project.status);
                return (
                  <div key={project.id} className="project-item">
                    <div className="project-info">
                      <h4>{project.name}</h4>
                      <p className="text-xs text-gray-500">
                        {project.clientName} • {formatRelativeTime(project.updatedAt)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`status-badge ${badge.class}`}>
                        {badge.label}
                      </span>
                      <span className="text-xs text-gray-500">
                        {project.progress}% • {formatCurrency(project.budget)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card">
          <h3>📊 Actividad Reciente</h3>
          {stats.recentActivity.length === 0 ? (
            <p className="text-gray-500 text-sm py-4">No hay actividad registrada</p>
          ) : (
            <div className="activity-list">
              {stats.recentActivity.map((activity) => (
                <div key={activity.id} className="activity-item">
                  <span className="activity-icon">📌</span>
                  <div>
                    <p className="activity-text">
                      {formatActivityAction(activity.action)}
                    </p>
                    <span className="activity-time">
                      {activity.userName} • {formatRelativeTime(activity.timestamp)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Documentos Recientes */}
      <div className="card" style={{ marginTop: '20px' }}>
        <h3>📄 Últimos Documentos</h3>
        {stats.recentDocuments.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">No hay documentos registrados</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Proyecto</th>
                  <th>Subido por</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentDocuments.map((doc) => {
                  const badge = getStatusBadge(doc.signed ? 'signed' : 'pending');
                  return (
                    <tr key={doc.id}>
                      <td className="font-medium">{doc.name}</td>
                      <td>{doc.projectName}</td>
                      <td>{doc.uploadedBy}</td>
                      <td>
                        <span className={`status-badge ${badge.class}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="text-sm text-gray-500">
                        {formatRelativeTime(doc.createdAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}